import AutoTableColumn from '@com/pt-components/pt-table/AutoTableColumn'
import {
  MetricsOption,
  PlaceAdsMetricsOption,
  allowedOnlyActionTrafficTypes,
  metricsOptions,
  plMetrics,
  placeAdsMetrics,
  placeAdsMetricsOptions,
  timeMetrics,
} from './config'
import { Breakdowns, Metrics, TrafficType } from '@pt/enums/insights'
import { EngineType } from '@pt/enums/common'
import { AdAccount, ReportPermissionStatus } from '@pt/api/user/type'
import type { EnumToUnion } from 'types'
import { formatMetricsValue } from './unit'
import type { AdAccountChannel } from '@pt/api/user/type'
import { ChannelReportPermission } from '@pt/enums/user'
import { authChannelList } from '@pt/views/pt-entry/common-setting/account-auth/config'
import { TrafficTypeValue } from './types'
import { TranslateResult } from 'vue-i18n'
import Vue from 'vue'

export const isOnlyAllowedAction = (
  trafficType: TrafficTypeValue | undefined,
): trafficType is typeof allowedOnlyActionTrafficTypes[number] =>
  Boolean(trafficType) &&
  allowedOnlyActionTrafficTypes.includes(trafficType as any)

export function getTableColumn(data: { label: string; value: string }[]) {
  return data.map((item) => {
    return {
      prop: item.value,
      key: item.value,
      label: item.label + (timeMetrics.includes(item.value) ? '(s)' : ''),
      sortable: 'custom',
      columnComponent: AutoTableColumn,
      visible: true,
      align: 'right',
      params: {
        innerHTML({ row }) {
          if (timeMetrics.includes(item.value)) {
            // 展示到s 四舍五入
            // @ts-ignore
            // return fmtTime(Math.round(row[item.value] / 1000) * 1000, '{i}:{s}')
            return Math.round((row[item.value] ?? 0) / 1000)
          } else if (
            // 非PS引擎制作的素材，「有效交互次数」在pt页面里，展示为"无数据"
            item.value === Metrics.vi &&
            row.engine_type !== EngineType.PlaySmart2 &&
            row.engine_type !== EngineType.PlaySmart
          ) {
            return '无数据'
          }
          // @ts-ignore
          return formatMetricsValue(row[item.value], item.value)
        },
      },
    }
  })
}

// 组件只包括mintegral
export function isOnlyMintegral(data: TrafficType[]): boolean {
  return data.length === 1 && data[0] === TrafficType.MSDK
}

export function tableColumnValue(value: string, options: any[]): string {
  const option = options.find((item) => item.value === value)

  return option?.label ?? '-'
}

export function getReportPermission(
  channels: AdAccountChannel | AdAccountChannel[],
): ChannelReportPermission {
  const requiredAuthChannelIds = authChannelList().map((c) => c.value)
  const _channels = (
    Array.isArray(channels) ? channels : [channels]
  ).map<AdAccountChannel>((c) => {
    const requiredAuth = requiredAuthChannelIds.includes(c.traffic_type)

    return {
      ...c,
      has_auth_account: requiredAuth ? c.has_auth_account : true,
      all_set_report_permission: requiredAuth
        ? c.all_set_report_permission
        : true,
      pull_report_data: requiredAuth ? c.pull_report_data : true,
    }
  })

  // 无广告主
  if (_channels.every((c) => !c.has_download_records)) {
    // 无数据
    return ChannelReportPermission.NoData
  }

  return ChannelReportPermission.Allow
}

export function checkReportPermission(
  list: AdAccount[],
): EnumToUnion<typeof ReportPermissionStatus> {
  if (list.length <= 0) {
    // 无广告主
    return ReportPermissionStatus.NOADV
  } else if (list.some((adv) => adv.report_permission === 0)) {
    // 存在未授权数据范围广告主
    return ReportPermissionStatus.NORANGE
  } else if (list.every((adv) => adv.report_permission === 3)) {
    // 无授权数据广告主
    return ReportPermissionStatus.NODATA
  }
  return ReportPermissionStatus.ALLOW
}

// 交互指标工具函数，交互指标为了保证唯一性添加了'p_'，针对交互埋点接口指标需要移除前缀

export function removeMetricPrefix(metric: string | string[]) {
  if (typeof metric === 'string') {
    return metric.replace('p_', '')
  }
  return metric.map((val) => val.replace('p_', ''))
}

interface task {
  rule: 'add' | 'remove'
  value: string | string[]
}

const MetricsArray = []
for (const key in Metrics) {
  MetricsArray.push(Metrics[key])
}

export function cookMetricPrefix(task: task) {
  // 检查指标是否命中埋点交互指标
  const checkIsMetric = (metric) => {
    return MetricsArray.includes(`p_${metric}`) || MetricsArray.includes(metric)
  }

  const cookPrefix = {
    add: (metric) => {
      if (checkIsMetric(metric)) {
        return `p_${metric}`
      }
      return metric
    },
    remove: (metric) => {
      if (checkIsMetric(metric)) {
        return metric.replace('p_', '')
      }
      return metric
    },
  }
  if (typeof task.value === 'string') {
    return cookPrefix[task.rule](task.value)
  } else if (typeof task.value === 'object') {
    return task.value.map((val) => cookPrefix[task.rule](val))
  }
  return task.value
}

export function getActivedBreakdowns(options: {
  params: any
  filterableBreakdowns: Breakdowns[]
}): { selected: Breakdowns[]; filtered: Breakdowns[] } {
  // 根据规则，获取接口参数字段的key
  const getFilterableParamKeyByBreakdowns = (b: Breakdowns): string => `${b}s`

  const { params: _params, filterableBreakdowns } = options
  // 选择的维度
  const selected: Breakdowns[] = [_params.breakdown]
  // 筛选弹窗的筛选维度
  const filtered: Breakdowns[] = filterableBreakdowns
    .map((breakdown) => {
      const k = getFilterableParamKeyByBreakdowns(breakdown)
      const value = _params[k as keyof typeof _params]
      return Array.isArray(value) && value.length > 0 ? breakdown : undefined
    })
    .filter((v): v is Breakdowns => Boolean(v))

  return { selected, filtered }
}

export type _MetricsOptions = {
  label: TranslateResult
  disabled?: boolean
  disabledReason?: TranslateResult
  options: Array<MetricsOption | PlaceAdsMetricsOption>
}

export const computedMetricsOptions = (options: {
  trafficType: TrafficTypeValue
  selectedMetrics: Array<(MetricsOption | PlaceAdsMetricsOption)['value']>
  selectedBreakdowns: Breakdowns[]
  filteredBreakdowns: Breakdowns[]
  root: Vue
}): _MetricsOptions[] => {
  const {
    trafficType,
    selectedMetrics,
    root,
    selectedBreakdowns,
    filteredBreakdowns,
  } = options
  // 投放数据支持指标
  const adOptions: _MetricsOptions = {
    label: root.$t('playTurbo.dashboard.metricsGroupType.ads'),
    options: placeAdsMetrics
      .map((m) => placeAdsMetricsOptions[m])
      .map((option) => ({
        ...option,
        disabled:
          selectedMetrics && selectedMetrics.length
            ? !selectedMetrics.includes(option.value)
            : false,
      })),
    disabled: true,
  }
  // 交互数据支持指标
  const plOptions: _MetricsOptions = {
    label: root.$t('playTurbo.dashboard.metricsGroupType.pl'),
    options: plMetrics
      .map((m) => metricsOptions[m])
      .map((option) => ({
        ...option,
        disabled:
          selectedMetrics && selectedMetrics.length
            ? !selectedMetrics.includes(option.value)
            : false,
      })),
    disabled: true,
  }

  const result: Array<_MetricsOptions> = [adOptions, plOptions]
  // 过滤指标函数
  const exclude = (_result: Array<_MetricsOptions>) =>
    result.filter((item) => !_result.includes(item))

  // 满足 bds 要有bs的元素 时候，返回true
  const isIncludeSelected = (
    bds: Breakdowns[],
    bs = [...selectedBreakdowns, ...filteredBreakdowns],
  ) => bds.some((item) => bs.includes(item))

  const setDisabledReason = (bds: Breakdowns[], options: _MetricsOptions[]) => {
    const selectedReason = !isIncludeSelected(bds, selectedBreakdowns)
      ? root.$t('playTurbo.dashboard.tips.selectedReason')
      : undefined
    const filteredReason = !isIncludeSelected(bds, filteredBreakdowns)
      ? root.$t('playTurbo.dashboard.tips.filteredReason')
      : undefined
    const reason = selectedReason || filteredReason
    options.forEach((option) => {
      if (option.disabled) {
        option.disabledReason = reason
      }
    })
  }

  // 「Mintegral」
  if (trafficType === TrafficType.Mintegral) {
    // 维度选择：广告呈现类型
    // ⬇
    // 选择「广告呈现类型」时，仅支持投放数据。

    // 维度选择：素材来源、可玩模板、项目、投放产品、广告系列、计划、地区、广告版位、创意、创意版本id(basic_id)，playturbo素材名称
    // ⬇
    // 「投放数据」或「交互数据」
    const bds: Breakdowns[] = [
      Breakdowns.material_name,
      Breakdowns.material_source,
      Breakdowns.template_id,
      Breakdowns.project_id,
      Breakdowns.package_name,
      Breakdowns.traffic_type,
      Breakdowns.ad_type,
      Breakdowns.ad_output_type,
      Breakdowns.basic_id,
      Breakdowns.creative_name,
      Breakdowns.adset_id,
      Breakdowns.campaign_id,
      Breakdowns.country_code,
      Breakdowns.ad_output_type,
      Breakdowns.fingerprint,
    ]

    if (isIncludeSelected([Breakdowns.ad_output_type])) {
      adOptions.disabled = false
      setDisabledReason([Breakdowns.ad_output_type], exclude([adOptions]))
    } else if (isIncludeSelected(bds)) {
      adOptions.disabled = false
      plOptions.disabled = false
      setDisabledReason(bds, exclude([adOptions, plOptions]))
    }

    return result
  } else if (isOnlyAllowedAction(trafficType)) {
    // 仅当交互数据指标
    plOptions.disabled = false
    return result
  }

  // 「汇总」或「Unity」

  // 维度选择：广告平台（仅当渠道选择为汇总时）、素材来源（Pt/Ps/定制项目）、可玩模板、项目、投放产品、广告系列、计划、地区，创意版本id(basic_id)，playturbo素材名称
  // ⬇
  // 投放指标
  const adbds: Breakdowns[] = [
    Breakdowns.traffic_type,
    Breakdowns.material_source,
    Breakdowns.template_id,
    Breakdowns.project_id,
    Breakdowns.basic_id,
    Breakdowns.creative_name,
    Breakdowns.package_name,
    Breakdowns.ad_id,
    Breakdowns.adset_id,
    Breakdowns.campaign_id,
    Breakdowns.country_code,
    Breakdowns.fingerprint,
    Breakdowns.material_name,
  ]
  if (isIncludeSelected(adbds)) {
    adOptions.disabled = false
  } else {
    setDisabledReason(adbds, [adOptions])
  }

  // 维度选择：广告平台（仅当渠道选择为汇总时）、素材来源、可玩模板、项目，创意版本id(basic_id)，playturbo素材名称
  // ⬇
  // 交互指标
  const plbds: Breakdowns[] = [
    Breakdowns.traffic_type,
    Breakdowns.material_source,
    Breakdowns.template_id,
    Breakdowns.project_id,
    Breakdowns.basic_id,
    Breakdowns.creative_name,
    Breakdowns.fingerprint,
    Breakdowns.material_name,
  ]
  if (isIncludeSelected(plbds)) {
    plOptions.disabled = false
  } else {
    setDisabledReason(plbds, [plOptions])
  }

  return result
}
